/**
 * ScheduleQuestionAssignment — Question picker for a schedule.
 *
 * Two modes:
 * 1. **View mode** (default) — Clean read-only list of assigned questions.
 *    Shows question text, tags, active/inactive status. No search/filter.
 * 2. **Edit mode** — Full picker with search, tag filter, checkbox to
 *    add/remove questions, active toggle, dirty tracking, save/reset.
 *
 * Protected questions (with audit responses) cannot be unassigned — only deactivated.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { useQuestions } from '../hooks/useQuestions';
import { useTags } from '../hooks/useTags';
import { useScheduleQuestions, useSyncScheduleQuestions } from '../hooks/useSchedules';
import type { LpaQuestionWithTags, LpaTag, SyncScheduleQuestionsInput } from '../types';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Checkbox } from '@/shared/ui/checkbox';
import { Switch } from '@/shared/ui/switch';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  Search,
  Filter,
  Save,
  RotateCcw,
  Loader2,
  ShieldAlert,
  ListChecks,
  Pencil,
  X,
  ChevronDown,
  Clock,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types for local state
// ---------------------------------------------------------------------------

interface LocalAssignment {
  question_id: string;
  is_active: boolean;
  sort_order: number;
}

// ---------------------------------------------------------------------------
// Tag filter bar
// ---------------------------------------------------------------------------

function TagFilterBar({
  tags,
  selectedTagIds,
  onToggle,
  onClear,
}: {
  tags: LpaTag[];
  selectedTagIds: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const { t, getTranslation } = useLanguage();
  const activeTags = tags.filter((tag) => tag.is_active);
  if (activeTags.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground font-medium mr-1">
        <Filter className="h-3 w-3 inline-block mr-1" />
        {t('mod_lpa.schedules.questions.filterByTag', 'Tags:')}
      </span>
      {activeTags.map((tag) => {
        const selected = selectedTagIds.includes(tag.id);
        return (
          <button key={tag.id} type="button" onClick={() => onToggle(tag.id)}>
            <Badge
              variant={selected ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              style={
                tag.color
                  ? selected
                    ? { backgroundColor: tag.color, borderColor: tag.color, color: '#fff' }
                    : { borderColor: tag.color, color: tag.color }
                  : undefined
              }
            >
              {getTranslation(tag.name_translations, tag.name)}
            </Badge>
          </button>
        );
      })}
      {selectedTagIds.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted-foreground underline hover:text-foreground ml-1"
        >
          {t('mod_lpa.schedules.questions.clearFilters', 'Clear')}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question row (edit mode — with checkbox + active toggle)
// ---------------------------------------------------------------------------

interface QuestionRowProps {
  question: LpaQuestionWithTags;
  isAssigned: boolean;
  isActive: boolean;
  isProtected: boolean;
  onToggleAssigned: () => void;
  onToggleActive: () => void;
  disabled: boolean;
  t: (key: string, fallback: string) => string;
  getTranslation: (translations: Record<string, string>, fallback: string) => string;
}

function QuestionRow({
  question,
  isAssigned,
  isActive,
  isProtected,
  onToggleAssigned,
  onToggleActive,
  disabled,
  t,
  getTranslation,
}: QuestionRowProps) {
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-md border ${
        isAssigned ? 'bg-accent/30 border-accent' : 'border-transparent hover:bg-muted/50'
      } ${!question.is_active ? 'opacity-50' : ''}`}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isAssigned}
        onCheckedChange={onToggleAssigned}
        disabled={disabled || isProtected}
      />

      {/* Question text */}
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">
          {getTranslation(question.question_translations, question.question)}
        </p>
        {question.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {question.tags.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                className="text-[10px] px-1 py-0"
                style={tag.color ? { borderColor: tag.color, color: tag.color } : undefined}
              >
                {getTranslation(tag.name_translations, tag.name)}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Protected icon */}
      {isProtected && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <ShieldAlert className="h-4 w-4 text-amber-500 shrink-0" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs">
              {t(
                'mod_lpa.schedules.questions.protectedHint',
                'This question has audit responses and cannot be removed. You can deactivate it instead.',
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Active toggle (only when assigned) */}
      {isAssigned && (
        <Switch
          checked={isActive}
          onCheckedChange={onToggleActive}
          disabled={disabled}
          className="scale-75 shrink-0"
        />
      )}

      {/* Inactive question bank badge */}
      {!question.is_active && (
        <Badge variant="secondary" className="text-[10px] shrink-0">
          {t('mod_lpa.schedules.questions.questionInactive', 'Inactive in bank')}
        </Badge>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only question row (view mode — simpler, no checkbox)
// ---------------------------------------------------------------------------

interface ViewQuestionRowProps {
  question: LpaQuestionWithTags;
  isActive: boolean;
  sortOrder: number;
  hasResponses: boolean;
  t: (key: string, fallback: string) => string;
  getTranslation: (translations: Record<string, string>, fallback: string) => string;
}

function formatDuration(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null;
  if (seconds < 60) return `${seconds}s`;
  return `${Math.round(seconds / 60)} min`;
}

function roundToNearestFiveMinutes(seconds: number): number {
  if (seconds <= 0) return 0;
  const rounded = Math.round(seconds / 300) * 300;
  return Math.max(300, rounded);
}

function ViewQuestionRow({
  question,
  isActive,
  sortOrder,
  hasResponses,
  t,
  getTranslation,
}: ViewQuestionRowProps) {
  const [isOpen, setIsOpen] = useState(false);

  const explanationText = getTranslation(
    question.explanation_translations,
    question.explanation ?? '',
  ).trim();
  const reactionPlanText = getTranslation(
    question.reaction_plan_translations,
    question.reaction_plan ?? '',
  ).trim();
  const hasDetails = explanationText.length > 0 || reactionPlanText.length > 0;
  const durationLabel = formatDuration(question.estimated_duration_seconds);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={`rounded-lg border transition-colors ${
          isOpen ? 'border-primary/40 bg-accent/10' : 'border-border hover:bg-muted/30'
        } ${!isActive ? 'opacity-70' : ''}`}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full text-left flex items-start gap-3 px-3 py-2.5"
          >
            <span className="h-5 w-5 rounded-full bg-muted text-[10px] font-semibold flex items-center justify-center shrink-0 mt-0.5">
              {sortOrder + 1}
            </span>

            {/* Question text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-snug">
                {getTranslation(question.question_translations, question.question)}
              </p>
              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                {question.tags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant="outline"
                    className="text-[10px] px-1 py-0"
                    style={tag.color ? { borderColor: tag.color, color: tag.color } : undefined}
                  >
                    {getTranslation(tag.name_translations, tag.name)}
                  </Badge>
                ))}
                {durationLabel && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground ml-1">
                    <Clock className="h-3 w-3" />
                    {durationLabel}
                  </span>
                )}
              </div>
            </div>

            {/* Status badges */}
            <div className="flex items-center gap-1.5 shrink-0">
              {!isActive && (
                <Badge variant="secondary" className="text-[10px]">
                  {t('mod_lpa.schedules.inactive', 'Inactive')}
                </Badge>
              )}
              {hasResponses && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[240px] text-xs">
                      {t(
                        'mod_lpa.schedules.questions.protectedHint',
                        'This question has audit responses and cannot be removed. You can deactivate it instead.',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t px-11 py-3 space-y-3 bg-muted/20">
            {explanationText && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  {t('mod_lpa.questions.explanation', 'Explanation / Guidance')}
                </p>
                <p className="text-sm whitespace-pre-wrap">{explanationText}</p>
              </div>
            )}

            {reactionPlanText && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  {t('mod_lpa.questions.reactionPlan', 'Reaction Plan')}
                </p>
                <p className="text-sm whitespace-pre-wrap">{reactionPlanText}</p>
              </div>
            )}

            {!hasDetails && (
              <p className="text-sm text-muted-foreground italic">
                {t('mod_lpa.questions.noDetails', 'No additional details for this question.')}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ScheduleQuestionAssignmentProps {
  scheduleId: string;
  auditId: string;
  isArchived: boolean;
}

export default function ScheduleQuestionAssignment({
  scheduleId,
  auditId,
  isArchived,
}: ScheduleQuestionAssignmentProps) {
  const { t, getTranslation } = useLanguage();

  // Data queries
  const { data: allQuestions, isLoading: questionsLoading } = useQuestions();
  const { data: tags = [] } = useTags();
  const { data: scheduleQuestions, isLoading: sqLoading } = useScheduleQuestions(scheduleId);
  const syncMutation = useSyncScheduleQuestions();

  // Edit mode toggle
  const [isEditing, setIsEditing] = useState(false);

  // Filter state (only used in edit mode)
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // Local assignment state (dirty tracking)
  const [localAssignments, setLocalAssignments] = useState<LocalAssignment[]>([]);
  const [baselineAssignments, setBaselineAssignments] = useState<LocalAssignment[]>([]);

  // Protected question IDs (have responses — can't be unassigned)
  const protectedQuestionIds = useMemo(() => {
    if (!scheduleQuestions) return new Set<string>();
    return new Set(
      scheduleQuestions
        .filter((sq) => sq.has_responses)
        .map((sq) => sq.question_id),
    );
  }, [scheduleQuestions]);

  const serverAssignments = useMemo(
    () =>
      (scheduleQuestions ?? []).map((sq) => ({
        question_id: sq.question_id,
        is_active: sq.is_active,
        sort_order: sq.sort_order,
      })),
    [scheduleQuestions],
  );

  // Keep local + baseline in sync while not actively editing.
  useEffect(() => {
    if (isEditing) return;
    setLocalAssignments(serverAssignments);
    setBaselineAssignments(serverAssignments);
  }, [serverAssignments, isEditing]);

  // Reset edit-only UI state when schedule changes
  useEffect(() => {
    setIsEditing(false);
    setSearchQuery('');
    setSelectedTagIds([]);
  }, [scheduleId]);

  // Dirty check
  const isDirty = useMemo(() => {
    if (localAssignments.length !== baselineAssignments.length) return true;

    const serverMap = new Map(
      baselineAssignments.map((a) => [a.question_id, a]),
    );

    return localAssignments.some((la) => {
      const sa = serverMap.get(la.question_id);
      if (!sa) return true;
      return la.is_active !== sa.is_active || la.sort_order !== sa.sort_order;
    });
  }, [localAssignments, baselineAssignments]);

  // Toggle tag filter
  const toggleTagFilter = useCallback((id: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  // Filter questions (edit mode — all questions from bank)
  const filteredQuestions = useMemo(() => {
    if (!allQuestions) return [];

    return allQuestions.filter((q) => {
      // Tag filter (AND logic)
      if (selectedTagIds.length > 0) {
        const qTagIds = new Set(q.tags.map((t) => t.id));
        if (!selectedTagIds.every((id) => qTagIds.has(id))) return false;
      }

      // Text search
      if (searchQuery.trim()) {
        const lower = searchQuery.toLowerCase();
        const matchesQuestion = q.question.toLowerCase().includes(lower);
        const matchesExplanation = q.explanation?.toLowerCase().includes(lower);
        if (!matchesQuestion && !matchesExplanation) return false;
      }

      return true;
    });
  }, [allQuestions, searchQuery, selectedTagIds]);

  // Questions resolved for view mode (assigned questions only, with full details)
  const assignedQuestionsForView = useMemo(() => {
    if (!allQuestions || !scheduleQuestions) return [];

    const questionMap = new Map(allQuestions.map((q) => [q.id, q]));

    return scheduleQuestions
      .map((sq) => {
        const question = questionMap.get(sq.question_id);
        return question
          ? {
              question,
              is_active: sq.is_active,
              has_responses: sq.has_responses,
              sort_order: sq.sort_order,
            }
          : null;
      })
      .filter(Boolean) as {
        question: LpaQuestionWithTags;
        is_active: boolean;
        has_responses: boolean;
        sort_order: number;
      }[];
  }, [allQuestions, scheduleQuestions]);

  // Lookup helpers
  const assignmentMap = useMemo(() => {
    const map = new Map<string, LocalAssignment>();
    for (const a of localAssignments) {
      map.set(a.question_id, a);
    }
    return map;
  }, [localAssignments]);

  // Handlers
  const toggleAssigned = (questionId: string) => {
    if (isArchived) return;
    const existing = assignmentMap.get(questionId);
    if (existing) {
      // Remove (unless protected)
      if (protectedQuestionIds.has(questionId)) return;
      setLocalAssignments((prev) =>
        prev.filter((a) => a.question_id !== questionId),
      );
    } else {
      // Add
      const maxSort = localAssignments.reduce(
        (max, a) => Math.max(max, a.sort_order),
        -1,
      );
      setLocalAssignments((prev) => [
        ...prev,
        { question_id: questionId, is_active: true, sort_order: maxSort + 1 },
      ]);
    }
  };

  const toggleActive = (questionId: string) => {
    if (isArchived) return;
    setLocalAssignments((prev) =>
      prev.map((a) =>
        a.question_id === questionId ? { ...a, is_active: !a.is_active } : a,
      ),
    );
  };

  const handleSave = () => {
    const assignments: SyncScheduleQuestionsInput[] = localAssignments.map((a) => ({
      question_id: a.question_id,
      sort_order: a.sort_order,
      is_active: a.is_active,
    }));

    syncMutation.mutate(
      { scheduleId, auditId, assignments },
      {
        onSuccess: () => {
          setIsEditing(false);
          setSearchQuery('');
          setSelectedTagIds([]);
        },
      },
    );
  };

  const handleStartEdit = () => {
    setLocalAssignments(serverAssignments);
    setBaselineAssignments(serverAssignments);
    setIsEditing(true);
  };

  const handleReset = () => {
    setLocalAssignments(baselineAssignments);
  };

  const handleCancelEdit = () => {
    handleReset();
    setIsEditing(false);
    setSearchQuery('');
    setSelectedTagIds([]);
  };

  // Counts
  const activeCount = localAssignments.filter((a) => a.is_active).length;
  const totalCount = localAssignments.length;

  const estimatedTimeRange = useMemo(() => {
    if (!allQuestions || localAssignments.length === 0) return null;

    const durationByQuestionId = new Map(
      allQuestions.map((q) => [q.id, q.estimated_duration_seconds ?? 0]),
    );

    const baseSeconds = localAssignments.reduce((sum, assignment) => {
      if (!assignment.is_active) return sum;
      const seconds = durationByQuestionId.get(assignment.question_id) ?? 0;
      return sum + Math.max(0, seconds);
    }, 0);

    if (baseSeconds <= 0) return null;

    const minSeconds = roundToNearestFiveMinutes(Math.abs(baseSeconds * 0.75));
    const maxSeconds = roundToNearestFiveMinutes(Math.abs(baseSeconds * 1.5));

    return {
      minMinutes: Math.round(minSeconds / 60),
      maxMinutes: Math.round(maxSeconds / 60),
    };
  }, [allQuestions, localAssignments]);

  const estimatedTimeLabel = useMemo(() => {
    if (!estimatedTimeRange) {
      return t('mod_lpa.schedules.questions.estimatedMissing', 'Est. n/a');
    }

    if (estimatedTimeRange.minMinutes === estimatedTimeRange.maxMinutes) {
      return t('mod_lpa.schedules.questions.estimatedSingle', 'Est. {value} min').replace(
        '{value}',
        String(estimatedTimeRange.minMinutes),
      );
    }

    return t('mod_lpa.schedules.questions.estimatedRange', 'Est. {min}-{max} min')
      .replace('{min}', String(estimatedTimeRange.minMinutes))
      .replace('{max}', String(estimatedTimeRange.maxMinutes));
  }, [estimatedTimeRange, t]);

  // Loading state
  if (questionsLoading || sqLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // View mode — clean read-only list of assigned questions
  // -------------------------------------------------------------------------
  if (!isEditing) {
    return (
      <div className="space-y-3">
        {/* Header with counts + Edit button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {t('mod_lpa.schedules.questions.title', 'Assigned Questions')}
            </span>
            <Badge variant="secondary" className="text-xs">
              {activeCount} {t('mod_lpa.schedules.questions.active', 'active')} / {totalCount} {t('mod_lpa.schedules.questions.total', 'total')}
            </Badge>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs">
                    {estimatedTimeLabel}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px] text-xs">
                  {t(
                    'mod_lpa.schedules.questions.estimatedHint',
                    'Estimate is calculated from active questions using 75%-150% of their configured durations, rounded to nearest 5 minutes.',
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {!isArchived && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  {t('mod_lpa.schedules.questions.edit', 'Edit')}
                </Button>
          )}
        </div>

        {/* Assigned question list */}
        {assignedQuestionsForView.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground rounded-lg border border-dashed">
            <p>{t('mod_lpa.schedules.questions.noneAssigned', 'No questions assigned yet.')}</p>
            {!isArchived && (
                <Button
                  variant="link"
                  size="sm"
                  className="mt-1"
                  onClick={handleStartEdit}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  {t('mod_lpa.schedules.questions.addQuestions', 'Add questions')}
                </Button>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {assignedQuestionsForView.map(({ question, is_active, has_responses, sort_order }) => (
              <ViewQuestionRow
                key={question.id}
                question={question}
                isActive={is_active}
                sortOrder={sort_order}
                hasResponses={has_responses}
                t={t}
                getTranslation={getTranslation}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Edit mode — full picker with search, filter, checkboxes
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-3">
      {/* Header with counts + save/reset/cancel */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {t('mod_lpa.schedules.questions.title', 'Assigned Questions')}
          </span>
          <Badge variant="secondary" className="text-xs">
            {activeCount} {t('mod_lpa.schedules.questions.active', 'active')} / {totalCount} {t('mod_lpa.schedules.questions.total', 'total')}
          </Badge>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-xs">
                  {estimatedTimeLabel}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] text-xs">
                {t(
                  'mod_lpa.schedules.questions.estimatedHint',
                  'Estimate is calculated from active questions using 75%-150% of their configured durations, rounded to nearest 5 minutes.',
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="flex items-center gap-2">
          {isDirty && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={syncMutation.isPending}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              {t('mod_lpa.schedules.questions.reset', 'Reset')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelEdit}
            disabled={syncMutation.isPending}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {t('mod_lpa.schedules.questions.cancelEdit', 'Cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={syncMutation.isPending || !isDirty}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1" />
            )}
            {t('mod_lpa.schedules.questions.save', 'Save')}
          </Button>
        </div>
      </div>

      {/* Search + tag filter */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('mod_lpa.schedules.questions.searchPlaceholder', 'Search questions...')}
            className="pl-9 h-8 text-sm"
            autoFocus
          />
        </div>
        <TagFilterBar
          tags={tags}
          selectedTagIds={selectedTagIds}
          onToggle={toggleTagFilter}
          onClear={() => setSelectedTagIds([])}
        />
      </div>

      {/* Question list (all from bank, with assignment checkboxes) */}
      <div className="max-h-[400px] overflow-y-auto space-y-1 pr-1">
        {filteredQuestions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {searchQuery || selectedTagIds.length > 0
              ? t('mod_lpa.schedules.questions.noMatch', 'No questions match the current filter.')
              : t('mod_lpa.schedules.questions.noQuestions', 'No questions in the question bank. Add questions first.')}
          </p>
        ) : (
          filteredQuestions.map((q) => {
            const assignment = assignmentMap.get(q.id);
            return (
              <QuestionRow
                key={q.id}
                question={q}
                isAssigned={!!assignment}
                isActive={assignment?.is_active ?? true}
                isProtected={protectedQuestionIds.has(q.id)}
                onToggleAssigned={() => toggleAssigned(q.id)}
                onToggleActive={() => toggleActive(q.id)}
                disabled={isArchived}
                t={t}
                getTranslation={getTranslation}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
